import React from "react";
import { Receipt } from "lucide-react";
import { cn } from "../../../lib/utils";
import { GatewayAuditApi } from "../Api/gatewayApi";
import { apiError } from "../utils/gatewayUtils";
import GatewayOboReceipt from "./GatewayOboReceipt";
import "../AgenticGateway.css";

function principalLabel(pr: any): string {
  if (!pr) return "?";
  if (typeof pr === "string") return pr;
  return pr.username || pr.clientId || (pr.id ? `${String(pr.id).slice(0, 8)}…` : pr.type || "?");
}

interface Hop {
  correlationId: string | null;
  fromFallback: string | null;
  toFallback: string | null;
  capFallback: string | null;
  aud: string | null;
  actor: string | null;
  scope: string | null;
  jti: string | null;
  ttlSeconds: number | null;
  decision: string | null;
  hasMint: boolean;
  chain: any[];
}

function buildHops(events: any[]): Hop[] {
  const order: string[] = [];
  const byHop: Record<string, any[]> = {};
  (events || []).forEach((e) => {
    const cid = e.correlationId || "__nohop__";
    if (!byHop[cid]) {
      byHop[cid] = [];
      order.push(cid);
    }
    byHop[cid].push(e);
  });

  const hops: Hop[] = [];
  order.forEach((cid) => {
    const evs = byHop[cid];
    const mint = evs.find((e) => e.eventType === "STS_TOKEN_MINTED");
    const p = (mint && (mint.responsePayload || mint.response_payload)) || {};
    let decision: string | null = null;
    for (const e of evs) {
      if (e.pdpDecision) { decision = e.pdpDecision; break; }
      if (e.status === "DENIED" || (e.eventType || "").includes("DENIED") || (e.eventType || "").includes("REJECTED")) { decision = "DENY"; break; }
      if (e.eventType === "CAPABILITY_ACCESS_GRANTED" || e.eventType === "PDP_DECISION_RENDERED") decision = "ALLOW";
    }
    const chain = Array.isArray(p.act_chain) ? p.act_chain : Array.isArray(p.actChain) ? p.actChain : [];
    if (!mint && !decision) return;
    let evFrom: string | null = null, evTo: string | null = null, evCap: string | null = null;
    for (const e of evs) {
      if (!evFrom && e.agentName) evFrom = String(e.agentName).replace(/ v\d.*$/, "");
      if (!evTo && e.serverName) evTo = e.serverName;
      if (!evCap && e.capabilityName) evCap = e.capabilityName;
    }
    hops.push({
      correlationId: cid !== "__nohop__" ? cid : null,
      fromFallback: evFrom,
      toFallback: evTo,
      capFallback: evCap,
      aud: p.aud || null,
      actor: p.act ? principalLabel(p.act) : chain.length ? principalLabel(chain[chain.length - 1]) : null,
      scope: p.scope || null,
      jti: p.jti || null,
      ttlSeconds: p.ttlSeconds != null ? p.ttlSeconds : null,
      decision,
      hasMint: !!mint,
      chain,
    });
  });
  return hops;
}

function OboReceiptPanel({ correlationId }: { correlationId: string }) {
  const [data, setData] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    GatewayAuditApi.oboReceipt(correlationId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: any) => {
        if (!cancelled) {
          const msg = /404|not found/i.test(e?.message || "")
            ? "No OBO token was minted for this hop."
            : apiError(e) || "Failed to load the token chain";
          setError(msg);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [correlationId]);

  if (loading) return <div className="text-[11px] text-muted-foreground py-2">Loading token chain…</div>;
  if (error) return <div className="text-[11px] text-muted-foreground py-2">{error}</div>;
  return (
    <div className="mt-2">
      <GatewayOboReceipt receipt={data} />
    </div>
  );
}

export default function GatewayConsoleHops({ events }: { events: any[] }) {
  const hops = React.useMemo(
    () => buildHops(events || []).filter((h) => h.hasMint || h.decision),
    [events]
  );
  const [openObo, setOpenObo] = React.useState<string | null>(null);
  if (!hops.length) return null;

  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Hops</div>
      <div className="space-y-2.5">
        {hops.map((h, i) => {
          const denied = h.decision === "DENY";
          const decLabel = h.decision || (h.hasMint ? "ALLOW" : "—");
          const scope = h.scope || h.capFallback || "";
          const proto =
            (h.scope && h.scope.indexOf("a2a:") === 0) || (h.capFallback && h.capFallback.indexOf(".") >= 0)
              ? "A2A SKILL"
              : (h.scope && h.scope.indexOf("mcp:") === 0) || h.capFallback
              ? "MCP TOOL"
              : "HOP";
          const from = h.actor || h.fromFallback || "?";
          const to = h.aud || h.toFallback || "?";
          const meta: string[] = [];
          if (h.chain && h.chain.length) meta.push(`chain ${h.chain.length}`);
          if (h.ttlSeconds != null) meta.push(`ttl ${h.ttlSeconds}s`);
          if (h.jti) meta.push(`jti ${String(h.jti).split("-")[0]}`);
          const open = !!h.correlationId && openObo === h.correlationId;
          return (
            <div key={i} className={cn("rounded-xl border px-3.5 py-3 ag-surface", denied ? "border-red-500/40" : "border-border")}>
              <div className="flex items-center justify-between gap-2.5 flex-wrap mb-2">
                <div className="flex items-center gap-2.5 flex-wrap min-w-0">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wider bg-primary/15 text-primary border border-primary/30">
                    {proto}
                  </span>
                  <strong className="text-[15px] text-foreground break-all">
                    {from} <span className="text-muted-foreground">→</span> {to}
                  </strong>
                  {meta.length > 0 && <span className="text-[12px] text-muted-foreground">{meta.join(" · ")}</span>}
                </div>
                <span
                  className={cn(
                    "inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border shrink-0",
                    denied ? "text-red-600 dark:text-red-400 border-red-500/40 bg-red-500/10" : "text-emerald-600 dark:text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
                  )}
                >
                  {decLabel}
                </span>
              </div>
              {scope && (
                <div className="font-mono text-[13px] text-primary ag-surface px-2 py-1 rounded inline-block break-all">
                  {scope}
                </div>
              )}
              {h.hasMint && h.correlationId && (
                <div className="mt-2.5">
                  <button
                    type="button"
                    onClick={() => setOpenObo(open ? null : h.correlationId)}
                    className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md border border-primary/30 bg-primary/10 text-primary hover:bg-primary/15 transition-colors"
                  >
                    <Receipt className="w-3 h-3" /> {open ? "Hide receipt" : "OBO receipt"}
                  </button>
                  {open && <OboReceiptPanel correlationId={h.correlationId} />}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
